import { COUNTRY_CODE } from '@/utils/constants';
import type { Serializable } from './Serializable';

/**
 * Project & Site info indicate where the SolShare is being commissioned.
 *
 * Can be either:
 * - Site selected by the user from the project list,
 * - Existing connected site that the SolShare is associated with, returned from the backend GET /upload-config API after SolShare is found.
 */
export interface SolshareSite {
  projectId: number;
  projectName: string | undefined | null;
  country: string;
  siteId: number;
  siteAddress: string;
  sitePostcode: string | null;
}

export interface ProjectWithSites {
  id: number;
  name: string | null;
  country: string;
  sites: Site[];
}

export interface Site {
  id: number;
  address: string;
  postcode: string | null | undefined;
  commissionedSolshareCount: number;
  plannedSolshareCount: number;
}

export interface WarrantyContact {
  company: string;
  firstName: string;
  lastName: string;
  contactEmail: string;
  contactPhone?: string;
}

export interface AssetManagerInvite {
  email: string;
  status?: EmailStatus;
}

export enum EmailStatus {
  Sending = 'sending',
  Sent = 'sent',
  Failed = 'failed',
}

export class ProjectFormModel implements Serializable<ProjectFormData> {
  id: number = 0;
  name: string | null = null;
  country: string = '';
  sites: SiteFormModel[] = [new SiteFormModel()];

  /**
   * Deserialize the project data from the backend.
   * @param data - The data that is returned from the backend.
   * @returns The project form model for UI consumption.
   */
  deserialize(data: ProjectFormData | null): this {
    if (data) {
      this.id = data.id;
      this.name = data.name;
      this.country = data.country;
      this.sites = data.sites.map((site: SiteFormData) => {
        const siteCountry = site.country ?? data.country;
        const siteWithLocation = { ...site, country: siteCountry };
        return new SiteFormModel().deserialize(siteWithLocation);
      });
    }
    return this;
  }

  /**
   * Serialize the project data to the backend.
   * @returns The serialized project data ready to be sent to the backend.
   */
  serialize(): unknown {
    const isCreatingProject = this.id === 0;

    /**
     * If the country is empty, set it to the first site country
     * default to Australia if no site country is set
     */
    if (!this.country && isCreatingProject) {
      this.country = this.sites[0]?.country ?? COUNTRY_CODE.Australia;
    }

    return {
      name: this.name,

      /**
       * For the meantime the project country is only set when creating a new project.
       * TODO: Revisit when we have a way to edit the project country.
       */
      ...(isCreatingProject ? { country: this.country } : {}),

      sites: this.sites.map((site) => site.serialize()),
    };
  }
}

export class SiteFormModel implements Serializable<SiteFormData> {
  id: number = 0;
  address: string = '';
  plannedSolshareCount: number = 1;
  commissionedSolshareCount: number = 0;

  /**
   * Longitude and latitude handling:
   *
   * 1/ number: Coordinates are known and explicitly set.
   *
   * 2/ null: Coordinates are intentionally unknown.
   *    Typically occurs when a user enters a custom address manually.
   *    In this case, longitude/latitude are stored as null in the database.
   *
   * 3/ undefined: Coordinates are not provided and must not be serialized.
   *    When data is loaded from the backend, long/lat may be absent.
   *    If the user makes no changes, omit these fields during serialization,
   *    signaling the backend to preserve existing database values.
   */
  latitude: number | null | undefined = undefined;
  longitude: number | null | undefined = undefined;

  country: string | null = null;
  postcode: string | null | undefined = undefined;
  persisted: boolean = false;

  /**
   * Deserialize the site data from the backend.
   * @param data - The data that is returned from the backend.
   * @returns The site form model for UI consumption.
   */
  deserialize(data: SiteFormData | null): this {
    if (data) {
      this.id = data.id;
      this.address = data.address;
      this.plannedSolshareCount = data.plannedSolshareCount;
      this.commissionedSolshareCount = data.commissionedSolshareCount ?? 0;
      this.latitude = data.latitude;
      this.longitude = data.longitude;
      this.postcode = data.postcode;
      this.country = data.country ?? null;
      this.persisted = data.persisted ?? true;
    }
    return this;
  }

  /**
   * Serialize the site data to the backend.
   * @returns The serialized site data ready to be sent to the backend.
   */
  serialize(): unknown {
    return {
      ...(this.persisted ? { id: this.id } : {}),
      ...(this.latitude !== undefined ? { latitude: this.latitude } : {}),
      ...(this.longitude !== undefined ? { longitude: this.longitude } : {}),
      address: this.address,
      postcode: this.postcode,
      plannedSolshareCount: this.plannedSolshareCount,
    };
  }
}

type SiteFormData = Site & {
  latitude?: number | null | undefined;
  longitude?: number | null | undefined;
  country?: string | null;
  postcode?: string | null;
  persisted?: boolean;
};

type ProjectFormData = ProjectWithSites & {
  sites: SiteFormData[];
};
